import { MapLayerId, MapSchema } from "@cartographerio/atlas-map";
import { point } from "@cartographerio/geometry";
import { baseStyles, productionMaps } from "@cartographerio/inventory-maps";
import { ChakraProvider } from "@chakra-ui/react";
import React, { ReactElement, useRef, useState } from "react";
import { Option } from "@cartographerio/fp/dist";
import { MapId } from "@cartographerio/types/dist";
import { mapboxAccessToken } from "../env";
import "./App.css";
import { parseAppQueryString } from "./appQueryString";
import BrowserGate from "./components/BrowserGate";
import FullScreen from "./components/FullScreen";
import Inspector from "./components/Inspector";
import useCartographerMap from "./hooks/map/useCartographerMap";
import useMapboxMap from "./hooks/useMapboxMap";

const query = parseAppQueryString(window.location, {
  map: undefined,
  apiUrl: undefined,
  layers: undefined,
  base: undefined,
  accessToken: undefined,
  defaultWorkspace: undefined,
  viewport: {
    center: point(-5, 53),
    zoom: 6,
    bearing: 0,
    pitch: 0,
  },
});

const inventory = productionMaps();

const schema: MapSchema = Option.wrap(query.map)
  .flatMap(mapId => inventory.mapById(mapId as MapId))
  .orElse(() =>
    Option.wrap(query.layers).map(layerIds =>
      inventory.unsafeMapFromLayerIds(layerIds as MapLayerId[])
    )
  )
  .getOrElse(() => inventory.unsafeKitchenSinkMap());

export default function App(): ReactElement {
  const {
    apiUrl,
    accessToken,
    defaultWorkspace,
    scrollWheel,
    viewport: defaultViewport,
  } = query;

  const { layers, defaultBaseStyleId } = schema;

  const container = useRef<HTMLDivElement>(null);

  const [baseStyleId, setBaseStyleId] = useState(
    defaultBaseStyleId ?? "terrain"
  );

  const map = useMapboxMap({
    container,
    mapboxAccessToken,
    baseStyles,
    baseStyleId,
    scrollWheel,
    defaultViewport,
  });

  const {
    layerVisibility,
    setLayerVisibility,
    selectedLayer,
    setSelectedLayer,
    currentAttributes,
    currentBuckets,
    setCurrentAttribute,
    selectedFeatures,
    setSelectedFeatures,
    setDateFilter,
  } = useCartographerMap({
    map,
    layers,
    baseStyles,
    apiUrl,
    accessToken,
    defaultWorkspace,
    baseStyleId,
  });

  return (
    <ChakraProvider>
      <FullScreen>
        <BrowserGate>
          <Inspector
            schema={schema}
            layerVisibility={layerVisibility}
            setLayerVisibility={setLayerVisibility}
            selectedLayer={selectedLayer}
            setSelectedLayer={setSelectedLayer}
            currentAttributes={currentAttributes}
            currentBuckets={currentBuckets}
            setCurrentAttribute={setCurrentAttribute}
            baseStyleId={baseStyleId}
            setBaseStyleId={setBaseStyleId}
            selectedFeatures={selectedFeatures}
            onClearFeatureSelection={() => setSelectedFeatures({})}
            onDateFilterChange={setDateFilter}
          />
          <FullScreen ref={container} />
        </BrowserGate>
      </FullScreen>
    </ChakraProvider>
  );
}
